import type {i18n as I18nInstance} from 'i18next'

export type TranslationModule = {
  namespace: string
  resources: Record<string, () => Promise<unknown>>
}

export function deduplicateTranslationModules(
  modules: TranslationModule[],
  options?: {debug?: boolean}
): TranslationModule[] {
  const {debug = false} = options ?? {}
  const seenNamespaces = new Map<string, TranslationModule>()

  for (const module of modules) {
    if (seenNamespaces.has(module.namespace)) {
      debug && console.info(`Duplicate translation module [${module.namespace}] found and skipped`)
    } else {
      seenNamespaces.set(module.namespace, module)
    }
  }

  return Array.from(seenNamespaces.values())
}

/**
 * Combines translation modules into a deduplicated array.
 * Accepts both single modules and arrays - they are flattened automatically.
 *
 * @example
 * // In the app - just list everything, no spreading needed:
 * const allTranslations = combineTranslationBundles([
 *   featureATranslations,  // array - works
 *   featureBTranslations,  // array - works
 *   someWidgetTranslations // single module - works
 * ])
 * await loadPackageTranslations(i18n, allTranslations)
 */
export function combineTranslationBundles(
  modules: Array<TranslationModule | TranslationModule[]>
): TranslationModule[] {
  return deduplicateTranslationModules(modules.flat())
}

export function getLanguagesToLoad(i18n: I18nInstance): Set<string> {
  const languages = new Set<string>()

  if (i18n.language) {
    languages.add(i18n.language)
  }

  const fallbackLng = i18n.options?.fallbackLng
  if (typeof fallbackLng === 'string') {
    languages.add(fallbackLng)
  } else if (Array.isArray(fallbackLng)) {
    for (const lng of fallbackLng) {
      if (typeof lng === 'string') {
        languages.add(lng)
      }
    }
  }

  return languages
}

// Tracks which bundles have been loaded by loadPackageTranslations.
const loadedBundles = new Set<string>()

function isBundleLoaded(lang: string, namespace: string): boolean {
  return loadedBundles.has(`${lang}:${namespace}`)
}

function markBundleLoaded(lang: string, namespace: string): void {
  loadedBundles.add(`${lang}:${namespace}`)
}

export function _resetLoadedBundles(): void {
  loadedBundles.clear()
}

export async function loadPackageTranslations(
  i18n: I18nInstance,
  modules: TranslationModule[],
  options?: {debug?: boolean}
) {
  const {debug = false} = options ?? {}

  const modulesToLoad = deduplicateTranslationModules(modules, {debug})
  const languagesToLoad = getLanguagesToLoad(i18n)

  const loadPromises = modulesToLoad.flatMap((module) =>
    Object.entries(module.resources)
      .filter(([lang]) => {
        if (!languagesToLoad.has(lang)) {
          debug &&
            console.info(`Skipped translations [${module.namespace}] for [${lang}] (not needed)`)
          return false
        }
        if (isBundleLoaded(lang, module.namespace)) {
          debug &&
            console.info(
              `Skipped translations [${module.namespace}] for [${lang}] (already loaded)`
            )
          return false
        }
        return true
      })
      .map(([lang, loaderFn]) =>
        loaderFn()
          .then((translations) => {
            const data = (translations as {default?: unknown}).default || translations
            i18n.addResourceBundle(lang, module.namespace, data, true, true)
            markBundleLoaded(lang, module.namespace)
            debug && console.info(`Loaded translations [${module.namespace}] for [${lang}]`)
          })
          .catch((err) => {
            debug && console.error(`Failed to load [${module.namespace}] for [${lang}]`, err)
          })
      )
  )

  await Promise.all(loadPromises)
}

export function setupTranslationLanguageSync(
  i18n: I18nInstance,
  modules: TranslationModule[],
  options?: {debug?: boolean}
) {
  const handler = () => {
    void loadPackageTranslations(i18n, modules, options)
  }

  i18n.on('languageChanged', handler)

  return () => {
    i18n.off('languageChanged', handler)
  }
}
